import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Home, User, Briefcase, Image, Mail } from 'lucide-react';

const navLinks = [
  { path: '/', label: 'Accueil', icon: Home },
  { path: '/qui-suis-je', label: 'Qui suis-je ?', icon: User },
  { path: '/prestations', label: 'Prestations', icon: Briefcase },
  { path: '/galerie', label: 'Galerie', icon: Image },
  { path: '/contact', label: 'Contact', icon: Mail },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <>
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'navbar-scrolled py-3' : 'py-4'
        }`}
        style={!scrolled ? { background: 'linear-gradient(to bottom, rgba(11,29,21,0.85) 0%, rgba(11,29,21,0.5) 60%, transparent 100%)' } : {}}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src="/images/logo.png" alt="Image & Riviere" className="h-14 transition-transform duration-300 group-hover:scale-105" />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-montserrat text-sm uppercase tracking-widest transition-colors duration-300 flex items-center gap-1.5 ${
                  location.pathname === link.path
                    ? 'text-[#7DD4F5]'
                    : 'text-[#8CA1B0] hover:text-[#F5F7F9]'
                }`}
              >
                <link.icon size={14} strokeWidth={1.5} />
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className="font-montserrat text-sm uppercase tracking-wider px-6 py-2.5 rounded-full border border-[#2B7BA8] text-[#7DD4F5] hover:bg-[#2B7BA8] hover:text-white transition-all duration-300"
            >
              Je prends rdv
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#F5F7F9] p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#0A1A27]/98 backdrop-blur-xl pt-24 px-8"
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                    className={`font-playfair text-3xl transition-colors ${
                      location.pathname === link.path ? 'text-[#7DD4F5]' : 'text-[#F5F7F9]'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating phone button */}
      <a
        href="tel:+33647786790"
        data-testid="floating-phone-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#2B7BA8] flex items-center justify-center shadow-lg shadow-[#2B7BA8]/30 hover:scale-110 transition-transform duration-300"
        style={{ animation: 'pulse-ring 2s ease-in-out infinite' }}
      >
        <Phone size={22} className="text-white" />
      </a>
    </>
  );
}
